import React, { memo, useCallback, useEffect, useState } from 'react';
import { getFileExtension, moneyFormat, onlyDigit } from '@app/helpers';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Card } from 'components/ui/card';
import { Textarea } from 'components/ui/textarea';
import { FilePicker, Grid, Paper } from 'components';
import { createReply } from '@app/api';
import { useNotification, useUserContext } from '@app/providers';
import * as yup from 'yup';
import { Reply, Tender } from '@app/models';
import { useForm } from '../hooks';
import { useTranslation } from 'react-i18next';

interface ReplyFormProps {
  tender: Tender | null;
  onSubmit: (reply?: Reply) => void;
}

interface Requirement {
  id: string;
  name: string;
  comment: string;
  checked: boolean;
}

interface ReplyFormData {
  price: string;
  description: string;
  files: File[];
  requirements: Requirement[];
}

function ReplyForm(props: ReplyFormProps) {
  const { tender, onSubmit } = props;
  const { company } = useUserContext();
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  const { values, onChange, validate, errors, setValues } =
    useForm<ReplyFormData>({
      price: '',
      description: '',
      files: [],
      requirements: [],
    });
  const [pending, setPending] = useState(false);

  const onChangePrice = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(onlyDigit(e.target.value), 'price');
    },
    [onChange]
  );

  const onChangeRequirement = useCallback(
    (requirementId: string) => (checked: boolean) => {
      onChange(
        values.requirements.map((requirement) => ({
          ...requirement,
          checked:
            requirement.id === requirementId ? checked : requirement.checked,
        })),
        'requirements'
      );
    },
    [onChange, values.requirements]
  );

  const onChangeRequirementComment = useCallback(
    (requirementId: string) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(
        values.requirements.map((requirement) => ({
          ...requirement,
          comment:
            requirement.id === requirementId
              ? e.target.value
              : requirement.comment,
        })),
        'requirements'
      );
    },
    [onChange, values.requirements]
  );

  const onChangeFiles = useCallback(
    (files: File[]) => {
      onChange(files, 'files');
    },
    [onChange]
  );

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        const { files, ...restValues } = values;

        const hasErrors = await validate(
          yup.object().shape({
            price: yup.string().required(t('required', { ns: 'validation' })!),
            description: yup
              .string()
              .required(t('required', { ns: 'validation' })!),
          })
        );

        if (hasErrors) {
          return;
        }

        const fileExtensions = files.map((file) =>
          getFileExtension(file).toLowerCase()
        );

        if (
          fileExtensions.indexOf('jpg') === -1 &&
          fileExtensions.indexOf('jpeg') === -1 &&
          fileExtensions.indexOf('png') === -1
        ) {
          showNotification({
            message: 'Загрузите хотябы 1 файл в формате jpg, jpeg или png',
            variant: 'error',
          });

          return;
        }

        setPending(true);

        const data: any = {
          ...restValues,
          tenderId: tender!.id,
          requirements: restValues.requirements.map((requirement) =>
            JSON.stringify({
              requirementId: requirement.id,
              comment: requirement.comment,
              checked: requirement.checked,
            })
          ),
        };

        if (!!company) {
          data.companyId = company.id;
        }

        const reply = await createReply(data, files);

        setPending(false);

        onSubmit(reply);

        showNotification({
          message: 'Отклик был успешно отправлен',
          variant: 'success',
        });
      } catch (e) {
        setPending(false);

        showNotification({
          message:
            'Ошибка не удалось отправить отклик. Обратитесь к администратору',
          variant: 'error',
        });
      }
    },
    [t, company, values, validate, tender, onSubmit, showNotification]
  );

  useEffect(() => {
    if (!!tender) {
      setValues({
        requirements: tender.requirements.map((requirement) => ({
          id: requirement.id,
          name: requirement.name,
          comment: '',
          checked: false,
        })),
      });
    }
  }, [setValues, tender]);

  return (
    <form className="overflow-y-auto" onSubmit={onSubmitForm}>
      <div className="space-y-2 md:space-y-4">
        <Card className="p-3 md:p-6">
          <h3 className="text-base font-medium mb-3 md:mb-4">
            Основная информация
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium mb-1"
              >
                Краткое содержание *
              </label>
              <Textarea
                id="description"
                value={values.description}
                onChange={(e) => onChange(e.target.value, 'description')}
                name="description"
                placeholder="Опишите ваше предложение по тендеру"
                className="min-h-[70px] resize-none"
                required
              />
              {errors.description && (
                <span className="text-red-500 text-xs">
                  {errors.description}
                </span>
              )}
            </div>
            <div>
              <label htmlFor="price" className="block text-sm font-medium mb-1">
                Предлагаемая сумма *
              </label>
              <Input
                id="price"
                value={moneyFormat(values.price)}
                onChange={onChangePrice}
                name="price"
                placeholder="Введите сумму"
                className="w-full"
                required
              />
              {errors.price && (
                <span className="text-red-500 text-xs">{errors.price}</span>
              )}
            </div>
          </div>
        </Card>

        {!!tender && values.requirements.length > 0 && (
          <Card className="p-3 md:p-6">
            <h3 className="text-base font-medium mb-3 md:mb-4">
              Дополнительные требования
            </h3>
            <div className="space-y-3 md:space-y-4">
              {values.requirements.map((requirement) => (
                <div key={requirement.id} className="space-y-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id={`req-check-${requirement.id}`}
                      checked={requirement.checked}
                      onCheckedChange={onChangeRequirement(requirement.id)}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor={`req-check-${requirement.id}`}
                      className="text-sm leading-relaxed cursor-pointer flex-1"
                    >
                      {requirement.name}
                    </label>
                  </div>
                  <div className="ml-6">
                    <Textarea
                      id={`req-comment-${requirement.id}`}
                      value={requirement.comment}
                      onChange={onChangeRequirementComment(requirement.id)}
                      placeholder="Комментарий..."
                      className="min-h-[60px] resize-none text-sm"
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card className="p-3 md:p-6">
          <h3 className="text-base font-medium mb-2 md:mb-3">Документы</h3>
          <p className="text-xs text-gray-600 mb-2 md:mb-3">
            Приложите документы в форматах JPG, JPEG или PNG
          </p>
          <FilePicker value={values.files} onChange={onChangeFiles} />
        </Card>

        <div className="flex justify-end px-3 md:px-0">
          <Button
            type="submit"
            disabled={pending}
            className="min-w-[140px]"
            size="lg"
          >
            {pending ? 'Отправка...' : 'Отправить отклик'}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default memo(ReplyForm);
